import { useNavigate } from 'react-router-dom';
import { Micro, SerifHeading } from '../primitives';
import { useUser } from '../../hooks/useUser';
import { coachForInsight } from './coachFromInsight';
import styles from './CoachStories.module.css';

export type CoachInsight = {
  id: string;
  label: string;
  color: string;
  glyph: string;
  text: string;
};

type CoachStoriesProps = {
  insights: CoachInsight[];
  /**
   * SP-8 coaches-alignment (today_density_v1) — right-size the carousel to the
   * density card grammar (founder: the section reads "disconnected and way too
   * big"). Dense = narrower cards, tighter padding, smaller type, shorter
   * min-height, so the strip subordinates to the ledger/other Today blocks
   * while staying reachable. Default false = byte-identical current cards.
   */
  dense?: boolean;
};

/**
 * Horizontal-scroll row of coach insight cards. Each card has a colored
 * glyph square, coach label in that color, and a single-sentence headline.
 *
 * B255 — a tap opens the chat with the coach who wrote the card (resolved
 * via `coachForInsight`), the card's text already seeded as that coach's
 * first message (carried in router history state, never the URL — see
 * `chatContextMode.ts`'s `readCoachCardEntry`). security F2 — the payload is
 * stamped with the TAPPING account's uid (`ownerId`, the B46 pattern in
 * `stampedSurfaceState.ts`); `readCoachCardEntry` reads it only for that
 * same viewer, so a reload, a sign-out/sign-in, or a browser-back into this
 * entry under a different account never seeds a foreign card. An insight
 * whose color doesn't match a known coach (shouldn't happen — every
 * rule/fixture sets one) falls back to an ordinary, unseeded chat rather
 * than guessing.
 *
 * Mobile-only composition; desktop uses a different "coach card + last
 * session" insights row pattern in step 7.
 */
export function CoachStories({ insights, dense }: CoachStoriesProps) {
  const navigate = useNavigate();
  const { user } = useUser();
  return (
    <div className={[styles.row, dense ? styles.rowDense : ''].filter(Boolean).join(' ')}>
      {insights.map((i) => {
        const coach = coachForInsight(i);
        return (
          <button
            key={i.id}
            type="button"
            className={[styles.story, dense ? styles.storyDense : ''].filter(Boolean).join(' ')}
            onClick={() =>
              navigate(
                '/chat',
                coach
                  ? {
                      state: {
                        coachCard: { coachId: coach.id, text: i.text, ownerId: user?.id ?? null },
                      },
                    }
                  : undefined,
              )
            }
            aria-label={`Open chat with ${coach ? coach.name : i.label}`}
          >
            <div className={styles.top}>
              <div className={styles.glyph} style={{ background: i.color }} aria-hidden>
                {i.glyph}
              </div>
              <Micro as="span" style={{ color: i.color }}>
                {i.label}
              </Micro>
            </div>
            <SerifHeading
              level="sm"
              as="div"
              className={[styles.text, dense ? styles.textDense : ''].filter(Boolean).join(' ')}
            >
              {i.text}
            </SerifHeading>
          </button>
        );
      })}
    </div>
  );
}
